import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { detectUserTimezone } from '@/utils/timezone-detection';

export interface UserTimezoneState {
  timezone: string | null;
  isConfirmed: boolean;
  isLoading: boolean;
  needsConfirmation: boolean;
  needsSetup: boolean;
  source: 'profile' | 'browser' | 'none';
  userType: 'client' | 'practitioner' | null;
  confirmTimezone: () => Promise<void>;
  updateTimezone: (newTimezone: string) => Promise<void>;
}

const SESSION_KEY = 'tz_detected';

export const useUserTimezone = (): UserTimezoneState => {
  const { user, activeRole } = useAuth();
  const userType = activeRole === 'practitioner' ? 'practitioner' : activeRole === 'client' ? 'client' : null;
  const [timezone, setTimezone] = useState<string | null>(null);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [source, setSource] = useState<'profile' | 'browser' | 'none'>('none');

  useEffect(() => {
    if (!user?.id || !userType) {
      setIsLoading(false);
      return;
    }

    const fetchTimezone = async () => {
      setIsLoading(true);
      try {
        const table = userType === 'practitioner' ? 'practitioners' : 'clients';
        const { data, error } = await supabase
          .from(table)
          .select('timezone, timezone_confirmed')
          .eq('id', user.id)
          .maybeSingle();

        if (error) {
          console.error('Error fetching timezone:', error);
        }

        if (data?.timezone && data.timezone.trim() !== '') {
          setTimezone(data.timezone);
          setIsConfirmed(data.timezone_confirmed === true);
          setSource('profile');
        } else {
          // Fallback to browser detection
          const cached = sessionStorage.getItem(SESSION_KEY);
          if (cached) {
            setTimezone(cached);
          } else {
            const detected = detectUserTimezone();
            sessionStorage.setItem(SESSION_KEY, detected);
            setTimezone(detected);
          }
          setIsConfirmed(false);
          setSource('browser');
        }
      } catch (err) {
        console.error('Error in useUserTimezone:', err);
        const detected = detectUserTimezone();
        setTimezone(detected);
        setSource('browser');
      } finally {
        setIsLoading(false);
      }
    };

    fetchTimezone();
  }, [user?.id, userType]);

  const confirmTimezone = useCallback(async () => {
    if (!user?.id || !userType || !timezone) return;

    const table = userType === 'practitioner' ? 'practitioners' : 'clients';
    const { error } = await supabase
      .from(table)
      .update({
        timezone,
        timezone_confirmed: true,
        updated_at: new Date().toISOString(),
      })
      .eq('id', user.id);

    if (!error) {
      setIsConfirmed(true);
      setSource('profile');
    }
  }, [user?.id, userType, timezone]);

  const updateTimezone = useCallback(async (newTimezone: string) => {
    if (!user?.id || !userType) return;

    const table = userType === 'practitioner' ? 'practitioners' : 'clients';
    const { error } = await supabase
      .from(table)
      .update({
        timezone: newTimezone,
        timezone_confirmed: true,
        updated_at: new Date().toISOString(),
      })
      .eq('id', user.id);

    if (!error) {
      setTimezone(newTimezone);
      setIsConfirmed(true);
      setSource('profile');
    }
  }, [user?.id, userType]);

  const needsSetup = source === 'browser' || source === 'none';
  const needsConfirmation = source === 'profile' && !isConfirmed;

  return {
    timezone,
    isConfirmed,
    isLoading,
    needsConfirmation,
    needsSetup,
    source,
    userType,
    confirmTimezone,
    updateTimezone,
  };
};
